"use client";

import React, { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, CheckCircle, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { apiPost } from "@/lib/api/client";

export function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus("error");
      return;
    }
    setStatus("loading");
    try {
      await apiPost("/newsletter/subscribe", { email });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  }, [email]);

  return (
    <form onSubmit={handleSubmit} className="mt-6 flex flex-col sm:flex-row items-center gap-2" noValidate>
      <div className="relative flex-1 w-full sm:w-auto">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <Input
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="pl-9 w-full sm:w-72"
          aria-label="Email for newsletter"
          aria-describedby={status === "error" ? "newsletter-error" : status === "success" ? "newsletter-success" : undefined}
          disabled={status === "loading" || status === "success"}
          required
        />
      </div>
      <Button type="submit" variant="premium" disabled={status === "loading" || status === "success" || !email}>
        {status === "loading" ? "Subscribing..." : status === "success" ? "Subscribed" : "Subscribe"}
      </Button>
      {status === "error" && (
        <p id="newsletter-error" className="text-xs text-destructive flex items-center gap-1" role="alert">
          <AlertCircle className="h-3 w-3" /> Please enter a valid email
        </p>
      )}
      {status === "success" && (
        <p id="newsletter-success" className="text-xs text-success flex items-center gap-1" role="status">
          <CheckCircle className="h-3 w-3" /> Welcome aboard!
        </p>
      )}
    </form>
  );
}